import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AktaLogo } from '../components/AktaLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useApp } from '../context/AppContext';
import { ShieldCheck, Lock, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { ROLES } from '../mock';
import FinancialMRIIllustration from '../components/FinancialMRIIllustration';

const AKTA_DOMAIN = 'aktawealth.com';
const roleRoute = (role) => role === ROLES.ADMIN ? '/admin' : role === ROLES.SALES_HEAD ? '/sales' : '/dashboard';
const roleLabel = (role) => role === ROLES.ADMIN ? 'Admin' : role === ROLES.SALES_HEAD ? 'Sales Head' : 'RM';

const DEMO = [
  { email: 'admin@aktawealth.com', name: 'Vikram Shah', role: ROLES.ADMIN },
  { email: 'rohan.sh@aktawealth.com', name: 'Rohan Kapadia', role: ROLES.SALES_HEAD },
  { email: 'divya.sh@aktawealth.com', name: 'Divya Trivedi', role: ROLES.SALES_HEAD },
  { email: 'akta@aktawealth.com', name: 'Akta Shah', role: ROLES.RM },
  { email: 'nikita@aktawealth.com', name: 'Nikita Joshi', role: ROLES.RM },
  { email: 'parth@aktawealth.com', name: 'Parth Bhavsar', role: ROLES.RM },
];

const LoginPage = () => {
  const nav = useNavigate();
  const { loginDemo } = useApp();
  const [email, setEmail] = React.useState('');
  const [pw, setPw] = React.useState('');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const doDemoLogin = async (e) => {
    setError('');
    if (!e || !e.endsWith(`@${AKTA_DOMAIN}`)) { setError(`Access restricted to @${AKTA_DOMAIN} accounts.`); return; }
    setBusy(true);
    try {
      const u = await loginDemo(e);
      nav(roleRoute(u.role));
    } catch (err) {
      setError(err?.apiMessage || 'Sign-in failed');
    } finally { setBusy(false); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmail = (e) => { e.preventDefault(); doDemoLogin(email.trim()); };

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1F135A 0%, #2E1F7A 60%, #160D40 100%)' }}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #60B74F 0%, transparent 40%), radial-gradient(circle at 80% 80%, #00B4E2 0%, transparent 40%)' }} />
          <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-lg bg-white flex items-center justify-center">
                <img src="/assets/akta-logo.png" className="h-11 object-contain" alt="Akta" />
              </div>
              <div>
                <div className="text-2xl font-bold">Akta Wealth</div>
                <div className="text-xs tracking-wider opacity-70">SUKH · SHANTI · SAMRUDDHI</div>
              </div>
            </div>
            <div className="max-w-md">
              <h1 className="text-4xl font-bold leading-tight mb-4">Financial MRI</h1>
              <p className="text-white/80 text-base leading-relaxed">A structured 9-module financial awareness &amp; suitability assessment tool, with role-based access for Admin, Retail Sales Heads &amp; Relationship Managers.</p>
              <div className="mt-6 space-y-3 text-sm text-white/80">
                <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-brand-green mt-0.5" /> AMFI-registered Mutual Fund Distributor</div>
                <div className="flex items-start gap-2"><Lock className="h-4 w-4 text-brand-green mt-0.5" /> Only @{AKTA_DOMAIN} accounts can sign in</div>
              </div>
            </div>

            {/* Brand illustration \u2014 subtle animated Financial MRI hero */}
            <div className="flex-1 -mx-6 -my-4 flex items-center justify-center min-h-[340px]">
              <FinancialMRIIllustration />
            </div>
            <div className="text-[11px] text-white/50">ARN-47282 · Not a SEBI Registered Investment Adviser</div>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8 flex items-center gap-3">
              <AktaLogo size={44} showText={false} />
              <div>
                <div className="font-bold text-brand-indigo text-xl">Akta Wealth</div>
                <div className="text-[10px] tracking-wider text-muted-foreground">FINANCIAL MRI</div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-brand-indigo">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-6">Use your Akta Wealth work account. Access is role-based.</p>

            <Button type="button" onClick={handleGoogle} className="w-full h-11 bg-white hover:bg-brand-bg text-brand-indigo border border-brand-border shadow-sm font-medium">
              <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.1 29.2 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.4 29 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.3-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.9 19 13 24 13c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.4 29 4.5 24 4.5c-7.3 0-13.5 3.9-17.7 10.2z"/>
                <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.8 13.2-5l-6.1-5c-2 1.4-4.5 2.5-7.1 2.5-5.2 0-9.6-2.9-11.3-7.1l-6.5 5C9.9 39.4 16.4 43.5 24 43.5z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.6l6.1 5C40.9 34.4 43.5 29.6 43.5 24c0-1.2-.1-2.3-.3-3.5z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="my-5 flex items-center gap-4">
              <div className="flex-1 h-px bg-brand-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-brand-border" />
            </div>

            <form onSubmit={handleEmail} className="space-y-4">
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" placeholder={`name@${AKTA_DOMAIN}`} />
              </div>
              <div>
                <Label htmlFor="pw">Password</Label>
                <Input id="pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 h-11" placeholder="••••••••" />
              </div>
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-xs">
                  <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
                </div>
              )}
              <Button type="submit" disabled={busy} className="w-full h-11 bg-brand-indigo hover:bg-brand-indigoLight text-white font-medium">{busy ? 'Signing in\u2026' : 'Sign in'}</Button>
            </form>

            <div className="mt-6">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">Demo accounts — tap to sign in</div>
              <div className="grid grid-cols-1 gap-2">
                {DEMO.map((u) => (
                  <button key={u.email} type="button" onClick={() => doDemoLogin(u.email)} className="text-left px-3 py-2 rounded-md border border-brand-border hover:bg-brand-bg transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-brand-indigo">{u.name}</div>
                        <div className="text-[11px] text-muted-foreground">{u.email}</div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-brand-indigo bg-brand-bg border border-brand-border px-2 py-0.5 rounded-full">{roleLabel(u.role)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 text-[11px] text-muted-foreground leading-relaxed">
              Internal access only. No public signup. All sessions are logged for AMFI / SEBI inspection compliance.
            </div>
          </div>
        </div>
      </div>
      <div className="px-6 py-3 text-[11px] text-brand-indigo/60 text-center">
        AMFI-registered Mutual Fund Distributor · ARN-47282 · Not a SEBI Registered Investment Adviser · Mutual Fund investments are subject to market risks.
      </div>
    </div>
  );
};

export default LoginPage;
